import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormField, FormLevel } from '../../utils/form-builder.utils';
import { isFolderEligibleField } from '../../utils/form-builder.utils';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// DOCU-746: Voreingestelltes Toggle-Button-Paar (an = grün mit Haken, aus = grau).
const TOGGLE_ON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 24">' +
  '<rect x="1" y="1" width="42" height="22" rx="11" fill="#2eb44e"/>' +
  '<circle cx="32" cy="12" r="9" fill="#ffffff"/>' +
  '<path d="M28.4 12.3l2.4 2.4 4.6-5.0" fill="none" stroke="#2eb44e" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const TOGGLE_OFF_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 24">' +
  '<rect x="1" y="1" width="42" height="22" rx="11" fill="#c4c4c4"/>' +
  '<circle cx="12" cy="12" r="9" fill="#ffffff"/></svg>';

@Component({
  selector: 'lib-field-config-panel',
  templateUrl: './field-config-panel.component.html',
  styleUrls: ['./field-config-panel.component.scss'],
})
export class FieldConfigPanelComponent implements OnChanges, OnInit, OnDestroy {
  @Input() field: FormField | null = null;
  @Input() level: FormLevel | null = null;
  @Input() isLevelSelected = false;

  @Output() fieldChanged = new EventEmitter<FormField>();
  @Output() levelChanged = new EventEmitter<FormLevel>();
  @Output() closePanel = new EventEmitter<void>();

  configForm: FormGroup;
  levelConfigForm: FormGroup;
  private destroy$ = new Subject<void>();
  private isPopulating = false;

  /**
   * DOCU-931: Der Export-Bereich startet eingeklappt. Seine Optionen sind
   * ausdrücklich Opt-in mit Default „aus" (DOCU-815/818) und werden von den
   * wenigsten Vorlagen genutzt, tragen aber die längsten Hinweistexte im ganzen
   * Panel — eingeklappt gewinnt es spürbar an Ruhe.
   *
   * Er öffnet sich aber von selbst, sobald an diesem Feld etwas gesetzt ist.
   * Sonst würde man dem Benutzer seine eigene Konfiguration verstecken, und das
   * wäre schlimmer als die lange Liste.
   */
  exportSectionOpen = false;

  constructor(private fb: FormBuilder, private sanitizer: DomSanitizer) {
    this.configForm = this.createForm();
    this.levelConfigForm = this.createLevelForm();
  }

  ngOnInit(): void {
    // Subscribe to field form changes with debounce
    this.configForm.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isPopulating) {
          this.emitFieldChange();
        }
      });

    this.levelConfigForm.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isPopulating) {
          this.emitLevelChange();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] && this.field) {
      const prevField = changes['field'].previousValue;
      if (!prevField || prevField.id !== this.field.id) {
        this.populateForm(this.field);
      }
    }
    if (changes['isLevelSelected'] || changes['level']) {
      if (this.isLevelSelected && this.level) {
        this.populateLevelForm(this.level);
      }
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      label: [''],
      hint: [''],
      required: [false],
      hidden: [false],
      // DOCU-669: Feld in der App nur anzeigen, nicht bearbeitbar
      readonly: [false],
      // Text/Textarea specific
      minLength: [null],
      maxLength: [null],
      allowPhoto: [false], // Allow photo attachment
      // Number specific
      min: [null],
      max: [null],
      decimalPlaces: [0],
      useThousandsSeparator: [false],
      unit: [''],
      // Photo specific
      maxFiles: [1],
      showTimestamp: [false],
      showGeoStamp: [false],
      writeGeoExif: [false],
      // DOCU-815: Kompakt-Export der Fotos
      exportColumns: [null],
      exportMaxImageCm: [null],
      // DOCU-818: Feld bei leer im Export ausblenden (feldtyp-übergreifend)
      exportHideWhenEmpty: [false],
      allowAnnotation: [false],
      // Select options
      options: this.fb.array([]),
      // Checkbox specific (DOCU-704/746): Export-Wertepärchen (Text + SVG-Visual)
      checkboxOptions: this.fb.group({
        trueLabel: [''],
        falseLabel: [''],
        trueSvg: [''],
        falseSvg: [''],
      }),
      // Initial value
      initialMode: ['none'], // 'none' | 'now' | 'fixed' — used for date/time radio UI
      initial: [null],       // Stores the fixed value for text/number/select, or fixed date/time
    });
  }

  populateForm(field: FormField): void {
    this.isPopulating = true;

    let initialMode = 'none';
    let initialValue: string | null = null;

    if (field.type === 'date' || field.type === 'time') {
      if (field.initial === 'now') {
        initialMode = 'now';
      } else if (field.initial) {
        initialMode = 'fixed';
        initialValue = field.initial;
      }
    } else {
      initialValue = field.initial ?? null;
    }

    this.configForm.patchValue({
      label: field.label || '',
      hint: field.hint || '',
      required: field.required || false,
      hidden: field.hidden || false,
      readonly: field.readonly || false,
      minLength: field.minLength,
      maxLength: field.maxLength,
      allowPhoto: field.allowPhoto || false,
      min: field.min,
      max: field.max,
      decimalPlaces: field.decimalPlaces ?? 0,
      useThousandsSeparator: field.useThousandsSeparator || false,
      unit: field.unit || '',
      maxFiles: field.maxFiles || 1,
      showTimestamp: field.showTimestamp || false,
      showGeoStamp: field.showGeoStamp || false,
      writeGeoExif: field.writeGeoExif || false,
      exportColumns: field.exportColumns ?? null,
      exportMaxImageCm: field.exportMaxImageCm ?? null,
      exportHideWhenEmpty: field.exportHideWhenEmpty || false,
      allowAnnotation: field.allowAnnotation || false,
      checkboxOptions: {
        trueLabel: field.checkboxOptions?.trueLabel ?? '',
        falseLabel: field.checkboxOptions?.falseLabel ?? '',
        trueSvg: field.checkboxOptions?.trueSvg ?? '',
        falseSvg: field.checkboxOptions?.falseSvg ?? '',
      },
      initialMode,
      initial: initialValue,
    });

    // DOCU-931: eingeklappt starten — ausser das Feld traegt bereits Export-Einstellungen.
    // Muss NACH patchValue laufen, weil hasExportSettings den Formularwert liest.
    this.exportSectionOpen = this.hasExportSettings;

    // Populate options for select fields
    const optionsArray = this.configForm.get('options') as FormArray;
    optionsArray.clear();
    if (field.options) {
      field.options.forEach((opt) => {
        optionsArray.push(this.fb.control(opt));
      });
    }
    
    // Allow form changes to be detected again after a tick
    setTimeout(() => {
      this.isPopulating = false;
    }, 0);
  }

  toggleExportSection(): void {
    this.exportSectionOpen = !this.exportSectionOpen;
  }

  /**
   * DOCU-931: Trägt dieses Feld eine vom Standard abweichende Export-Einstellung?
   * Steuert das automatische Aufklappen und die Markierung am eingeklappten Bereich.
   * `exportMaxImageCm` und `exportColumns` sind bewusst gegen `null` geprüft und
   * nicht auf Wahrheitswert — eine 0 wäre zwar unsinnig, aber gesetzt.
   */
  get hasExportSettings(): boolean {
    const v = this.configForm.value;
    return !!v.exportHideWhenEmpty || v.exportMaxImageCm != null || v.exportColumns != null;
  }

  // DOCU-669: readonly + required ist im Formular nicht auflösbar — die App kann
  // ein leeres, gesperrtes Pflichtfeld nicht befüllen. Hier nur als Hinweis;
  // blockiert wird beim Speichern des Templates (form-builder-editor).
  get isReadonlyRequiredConflict(): boolean {
    return !!this.configForm.get('readonly')?.value && !!this.configForm.get('required')?.value;
  }

  get optionsArray(): FormArray {
    return this.configForm.get('options') as FormArray;
  }

  addOption(): void {
    this.optionsArray.push(this.fb.control(''));
    this.onFormChange();
  }

  removeOption(index: number): void {
    this.optionsArray.removeAt(index);
    this.onFormChange();
  }

  // Called when an option value changes
  onOptionChange(): void {
    this.emitFieldChange();
  }

  // Called by checkboxes directly for immediate feedback
  onFormChange(): void {
    this.emitFieldChange();
  }

  // DOCU-704: füllt das Wertepärchen mit den voreingestellten Checkbox-Glyphen ☑/☐.
  applyCheckboxGlyphPreset(): void {
    this.configForm.get('checkboxOptions')?.patchValue({ trueLabel: '☑', falseLabel: '☐' });
    this.onFormChange();
  }

  // DOCU-746: füllt das SVG-Paar mit dem voreingestellten Toggle-Button (an/aus).
  applyToggleSvgPreset(): void {
    this.configForm.get('checkboxOptions')?.patchValue({ trueSvg: TOGGLE_ON_SVG, falseSvg: TOGGLE_OFF_SVG });
    this.onFormChange();
  }

  // DOCU-746: leert das SVG-Paar wieder (zurück auf Text/Standard).
  clearToggleSvgPreset(): void {
    this.configForm.get('checkboxOptions')?.patchValue({ trueSvg: '', falseSvg: '' });
    this.onFormChange();
  }

  // DOCU-746: Live-Vorschau der hinterlegten SVGs. Im Browser ist SVG-Inline
  // unproblematisch (der Export rastert es serverseitig). Admin-authored -> ok.
  private previewSvg(control: string): SafeHtml | null {
    const svg = (this.configForm.get(['checkboxOptions', control])?.value ?? '').trim();
    return svg ? this.sanitizer.bypassSecurityTrustHtml(svg) : null;
  }
  get trueSvgPreview(): SafeHtml | null { return this.previewSvg('trueSvg'); }
  get falseSvgPreview(): SafeHtml | null { return this.previewSvg('falseSvg'); }

  private emitFieldChange(): void {
    if (!this.field) return;

    const formValue = this.configForm.value;
    const updatedField: FormField = {
      ...this.field,
      label: formValue.label,
      hint: formValue.hint,
      required: formValue.required,
      hidden: formValue.hidden || null,
      // DOCU-669: nur ein gesetztes Häkchen persistieren (Default = bearbeitbar)
      readonly: formValue.readonly || null,
      // DOCU-818: feldtyp-übergreifend (in der Common-Sektion)
      exportHideWhenEmpty: formValue.exportHideWhenEmpty || null,
    };

    // Apply type-specific values
    switch (this.field.type) {
      case 'text':
      case 'textarea':
        updatedField.minLength = formValue.minLength;
        updatedField.maxLength = formValue.maxLength;
        updatedField.allowPhoto = formValue.allowPhoto;
        updatedField.initial = formValue.initial || null;
        break;
      case 'number':
        updatedField.min = formValue.min;
        updatedField.max = formValue.max;
        updatedField.decimalPlaces = formValue.decimalPlaces;
        updatedField.useThousandsSeparator = formValue.useThousandsSeparator;
        updatedField.unit = formValue.unit || undefined;
        updatedField.allowPhoto = formValue.allowPhoto;
        updatedField.initial = formValue.initial || null;
        break;
      case 'date':
      case 'time':
        updatedField.allowPhoto = formValue.allowPhoto;
        if (formValue.initialMode === 'now') {
          updatedField.initial = 'now';
        } else if (formValue.initialMode === 'fixed') {
          updatedField.initial = formValue.initial || null;
        } else {
          updatedField.initial = null;
        }
        break;
      case 'checkbox':
        updatedField.allowPhoto = formValue.allowPhoto;
        updatedField.checkboxOptions = {
          trueLabel: formValue.checkboxOptions?.trueLabel ?? '',
          falseLabel: formValue.checkboxOptions?.falseLabel ?? '',
          trueSvg: formValue.checkboxOptions?.trueSvg ?? '',
          falseSvg: formValue.checkboxOptions?.falseSvg ?? '',
        };
        break;
      case 'photo':
        updatedField.maxFiles = formValue.maxFiles;
        updatedField.showTimestamp = formValue.showTimestamp || false;
        updatedField.showGeoStamp = formValue.showGeoStamp || false;
        updatedField.writeGeoExif = formValue.writeGeoExif || false;
        // DOCU-815: leere/0-Eingaben = kein Opt-in (Renderer nutzt Default)
        updatedField.exportColumns = formValue.exportColumns || null;
        updatedField.exportMaxImageCm = formValue.exportMaxImageCm || null;
        updatedField.allowAnnotation = formValue.allowAnnotation || false;
        break;
      case 'signature':
        // Keine feldspezifischen Optionen für MVP
        break;
      case 'select':
        updatedField.options = this.optionsArray.value;
        updatedField.allowPhoto = formValue.allowPhoto;
        updatedField.initial = formValue.initial || null;
        break;
    }

    this.fieldChanged.emit(updatedField);
  }

  // --- Level form ---

  createLevelForm(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      description: [''],
      titleFieldId: ['', Validators.required],
      subtitleFieldId: [''],
      // DOCU-745: Feld, das den Ordnernamen füllt (nur Root-Ebene)
      folderFieldId: [''],
      // DOCU-664 / DOCU-625: DOCX-Export-Steuerung pro Level
      exportFormat: ['keyvalue'],
      exportTableMarker: [''],
      // DOCU-815/818: Prüfpunkt zusammenhalten / leeren Block ausblenden
      exportKeepTogether: [false],
      exportHideWhenEmpty: [false],
    });
  }

  populateLevelForm(level: FormLevel): void {
    this.isPopulating = true;
    this.levelConfigForm.patchValue({
      name: level.name || '',
      description: level.description || '',
      titleFieldId: level.titleFieldId || '',
      subtitleFieldId: level.subtitleFieldId || '',
      folderFieldId: this.getCurrentFolderFieldId(level),
      exportFormat: level.exportFormat || 'keyvalue',
      exportTableMarker: level.exportTableMarker || '',
      exportKeepTogether: level.exportKeepTogether || false,
      exportHideWhenEmpty: level.exportHideWhenEmpty || false,
    });
    setTimeout(() => {
      this.isPopulating = false;
    }, 0);
  }

  onLevelFormChange(): void {
    this.emitLevelChange();
  }

  private emitLevelChange(): void {
    if (!this.level) return;

    const formValue = this.levelConfigForm.value;

    // DOCU-745: apply the folder-name field selection (root level only). The
    // folder key lives on the field (folderLevel=1), so we mutate the shared
    // field objects here; at most one field may carry it, others are cleared.
    if (this.level.id === 'root') {
      const folderFieldId = formValue.folderFieldId || '';
      for (const f of this.level.fields) {
        if (f.id === folderFieldId) {
          f.folderLevel = 1;
        } else if (f.folderLevel != null) {
          f.folderLevel = null;
        }
      }
    }

    const updatedLevel: FormLevel = {
      ...this.level,
      name: formValue.name,
      description: formValue.description,
      titleFieldId: formValue.titleFieldId || undefined,
      subtitleFieldId: formValue.subtitleFieldId || undefined,
      // DOCU-664 / DOCU-625: DOCX-Export-Steuerung
      exportFormat: formValue.exportFormat || 'keyvalue',
      exportTableMarker: formValue.exportTableMarker?.trim() || undefined,
      // DOCU-815/818: Prüfpunkt zusammenhalten / leeren Block ausblenden
      exportKeepTogether: formValue.exportKeepTogether || undefined,
      exportHideWhenEmpty: formValue.exportHideWhenEmpty || undefined,
    };

    this.levelChanged.emit(updatedLevel);
  }

  getTitleEligibleFields(level: FormLevel | null): FormField[] {
    if (!level) return [];
    const allowed = ['text', 'textarea', 'date', 'time', 'select', 'number'];
    return level.fields.filter(f => allowed.includes(f.type));
  }

  // DOCU-745: fields eligible as folder key — root level only, text/number/select.
  getFolderEligibleFields(level: FormLevel | null): FormField[] {
    if (!level || level.id !== 'root') return [];
    return level.fields.filter(f => isFolderEligibleField(f));
  }

  private getCurrentFolderFieldId(level: FormLevel): string {
    return level.fields.find(f => f.folderLevel === 1)?.id || '';
  }

  // --- Common ---

  close(): void {
    this.closePanel.emit();
  }

  getFieldTypeLabel(type: string): string {
    const typeLabels: Record<string, string> = {
      text: 'FORM_BUILDER.FIELD_TYPES.TEXT',
      textarea: 'FORM_BUILDER.FIELD_TYPES.TEXTAREA',
      number: 'FORM_BUILDER.FIELD_TYPES.NUMBER',
      date: 'FORM_BUILDER.FIELD_TYPES.DATE',
      time: 'FORM_BUILDER.FIELD_TYPES.TIME',
      select: 'FORM_BUILDER.FIELD_TYPES.SELECT',
      checkbox: 'FORM_BUILDER.FIELD_TYPES.CHECKBOX',
      photo: 'FORM_BUILDER.FIELD_TYPES.PHOTO',
      signature: 'FORM_BUILDER.FIELD_TYPES.SIGNATURE',
      divider: 'FORM_BUILDER.FIELD_TYPES.DIVIDER',
    };
    return typeLabels[type] || type;
  }

  getFieldTypeIcon(type: string): string {
    const iconMap: Record<string, string> = {
      text: 'text_fields',
      textarea: 'notes',
      number: 'pin',
      date: 'calendar_today',
      time: 'schedule',
      select: 'arrow_drop_down_circle',
      checkbox: 'check_box',
      photo: 'photo_camera',
      divider: 'horizontal_rule',
    };
    return iconMap[type] || 'help_outline';
  }
}
